import { useState, type MouseEvent } from 'react';
import {
  Button,
  Icon,
  Popover,
  cn,
  shouldInterceptClick,
  testIdProp,
  MODULE_SQUARE_DECLINED_LINE,
  MODULE_SQUARE_DECLINED_NO_NOTE,
  MODULE_SQUARE_DECLINED_WHY,
  MODULE_SQUARE_NO_ACCESS_LINE,
  MODULE_SQUARE_REQUEST_LINE,
  MODULE_SQUARE_REQUESTED_LINE,
  MODULE_SQUARE_SETTING_UP_LINE,
  MODULE_SQUARE_TURN_ON_LINE,
} from '@givmo/design-system';
import {
  ORG_TIER_UNKNOWN_LINE,
  useModuleVerdict,
  type ModuleGateProps,
  type ModuleGateVerdict,
} from '@givmo/org-console';
import { ArrowRight, CircleAlert, Lock } from 'lucide-react';
import { FUNDRAISERS_ROUTES, FUNDRAISERS_SLUG } from './fundraisers-routes';

/** Facts/actions supplied by the host's existing standing read and request door. */
export type FundraiserModuleNavigation = Pick<
  ModuleGateProps,
  'read' | 'mounted' | 'onRetry' | 'turnOnHref' | 'onNavigate' | 'onRequestAccess' | 'accessRequest'
>;

export interface FundraiserModuleLinkProps extends FundraiserModuleNavigation {
  fundraiserId: string;
  testId?: string;
  /** Financial-table cells use the same affordance as a small internal-arrow icon. */
  iconOnly?: boolean;
  className?: string;
}

/** Navigation only. The destination independently authorizes its record and actions. */
export function FundraiserModuleLink({
  fundraiserId,
  read,
  mounted,
  onRetry,
  turnOnHref,
  onNavigate,
  onRequestAccess,
  accessRequest,
  iconOnly = false,
  className,
  testId = 'fundraiser-module-link',
}: FundraiserModuleLinkProps) {
  const verdict = useModuleVerdict(FUNDRAISERS_SLUG, read, { mounted });
  if (verdict === 'pending' || verdict === 'absent' || fundraiserId.length === 0) return null;
  const controlClass = cn(
    'inline-flex shrink-0 items-center gap-6 rounded-md text-s13 font-w500 outline-none transition-colors focus-visible:shadow-ring',
    iconOnly ? 'size-20 justify-center max-sm:size-44' : 'min-h-44 justify-start',
    className,
  );
  const navigate = (event: MouseEvent<HTMLAnchorElement>, href: string) => {
    if (!onNavigate || !shouldInterceptClick(event)) return;
    event.preventDefault();
    onNavigate(href);
  };
  if (verdict === 'link') {
    const href = FUNDRAISERS_ROUTES.editor(fundraiserId);
    return (
      <a
        href={href}
        onClick={(event) => navigate(event, href)}
        aria-label="Open in Fundraisers"
        title="Open in Fundraisers"
        className={cn(controlClass, 'text-brand-600 hover:text-brand-700')}
        {...testIdProp(testId)}
      >
        {!iconOnly && 'Open in Fundraisers'}
        <Icon icon={ArrowRight} size={14} aria-hidden />
      </a>
    );
  }
  return (
    <FundraiserAccessNotice
      key={fundraiserId}
      {...{
        read,
        onRetry,
        turnOnHref,
        onNavigate,
        onRequestAccess,
        accessRequest,
        iconOnly,
        testId,
        controlClass,
      }}
      verdict={verdict}
    />
  );
}

function FundraiserAccessNotice({
  read,
  onRetry,
  turnOnHref,
  onNavigate,
  onRequestAccess,
  accessRequest,
  iconOnly,
  testId,
  controlClass,
  verdict,
}: Omit<FundraiserModuleLinkProps, 'fundraiserId' | 'mounted'> & {
  testId: string;
  controlClass: string;
  verdict: Exclude<ModuleGateVerdict, 'link' | 'pending' | 'absent'>;
}) {
  const [open, setOpen] = useState(false);
  const navigate = (event: MouseEvent<HTMLAnchorElement>, href: string) => {
    if (!onNavigate || !shouldInterceptClick(event)) return;
    event.preventDefault();
    setOpen(false);
    onNavigate(href);
  };
  const held = read.standings?.[FUNDRAISERS_SLUG]?.held === true;
  const requestDoor = held ? undefined : onRequestAccess;
  const action = (label: string, callback: () => void, suffix: string) => (
    <Button
      appearance="saas"
      tone="secondary"
      size="sm"
      className="min-h-44"
      testId={`${testId}-${suffix}`}
      onClick={() => {
        setOpen(false);
        callback();
      }}
    >
      {label}
    </Button>
  );
  const settingsLink = (label: string, suffix: string) =>
    turnOnHref ? (
      <a
        href={turnOnHref}
        onClick={(event) => navigate(event, turnOnHref)}
        className="inline-flex min-h-44 min-w-44 items-center text-s13 font-w500 text-brand-600 outline-none hover:text-brand-700 focus-visible:shadow-ring"
        {...testIdProp(`${testId}-${suffix}`)}
      >
        {label}
      </a>
    ) : null;
  const requestAction =
    accessRequest?.status === 'pending' ? (
      <p className="text-s13 text-text-secondary" {...testIdProp(`${testId}-requested`)}>
        {MODULE_SQUARE_REQUESTED_LINE}
      </p>
    ) : (
      <>
        {accessRequest?.status === 'declined' && (
          <div
            className="space-y-6 text-s13 text-text-secondary"
            {...testIdProp(`${testId}-declined`)}
          >
            <p className="font-w600 text-text-strong">{MODULE_SQUARE_DECLINED_LINE}</p>
            <p>
              <span className="font-w600">{MODULE_SQUARE_DECLINED_WHY}: </span>
              {(accessRequest.note ?? '').trim()
                ? `“${accessRequest.note!.trim()}”`
                : MODULE_SQUARE_DECLINED_NO_NOTE}
            </p>
          </div>
        )}
        {requestDoor && action(MODULE_SQUARE_REQUEST_LINE, requestDoor, 'request')}
      </>
    );
  const unavailable = verdict === 'unavailable';
  const description = unavailable
    ? ORG_TIER_UNKNOWN_LINE
    : verdict === 'setting-up'
      ? MODULE_SQUARE_SETTING_UP_LINE
      : MODULE_SQUARE_NO_ACCESS_LINE;
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger
        aria-label={unavailable ? 'Fundraisers access unavailable' : 'Fundraisers access'}
        className={cn(controlClass, 'text-text-secondary hover:text-text-strong')}
        {...testIdProp(`${testId}-${verdict}`)}
      >
        <Icon icon={unavailable ? CircleAlert : Lock} size={14} aria-hidden />
        {!iconOnly && 'Fundraisers access'}
      </Popover.Trigger>
      <Popover.Content
        align="end"
        collisionPadding={16}
        className="w-[280px] max-w-[calc(100vw-32px)] p-16"
        testId={`${testId}-notice`}
      >
        <Popover.Title className="text-s14 font-w600 text-text-strong">Fundraisers</Popover.Title>
        <Popover.Description className="mt-8 text-s13 leading-h15 text-text-secondary">
          {description}
        </Popover.Description>
        <div className="mt-12 space-y-12">
          {unavailable
            ? onRetry && action('Try again', onRetry, 'retry')
            : verdict === 'setting-up'
              ? settingsLink('View setup', 'setup')
              : verdict === 'off' && turnOnHref
                ? settingsLink(MODULE_SQUARE_TURN_ON_LINE, 'turn-on')
                : requestAction}
        </div>
      </Popover.Content>
    </Popover.Root>
  );
}
